import { observer } from 'mobx-react'
import { useContext, useEffect, useState } from 'react'
import { Button, Icon, LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL, JOINTSIGNING_SERVICE_PATH } from '@/constants/apiUrl'
import { devLog } from '@/methods/devLog'
import { forceRedirect } from '@/methods/forceRedirect'
import { cx } from '@/utils/cx'
import { getVerifiedErrorReason } from '@/constants/verifiedTypes'
import { VerifiedErrorEnpalPage } from './VerifiedErrorEnpalPage'
import '../dataCollection/DataCollection.css'

interface GetJointSigningContextResponse {
  waitingOnSignatories: Array<{
    id: string
    status: string
    firstName?: string
    lastName?: string
    nextSignatoryUrl?: string
  }>
  enableNextSignatory?: boolean
}

/**
 * Completion screen of the Enpal affordability-check flows, the `verified-affordability-check`
 * scenario. Same look as the data-collection completion screen (nothing is signed in these flows,
 * so unlike the `success` / `verified` screens it never mentions a signature). When joint-signing's
 * get-context names a pending signatory who may continue on this device, a primary "continue as …"
 * button is added above the "complete" one, which then becomes secondary.
 */
export const SuccessAffordabilityCheckEnpalPage = observer(() => {
  const store = useContext(StoreContext)
  const { SuccessAffordabilityCheckEnpalPage: trans } = store.TranslationsState.translations

  const [isLoading, setIsLoading] = useState(true)
  const [nextSignatory, setNextSignatory] = useState<{ fullName: string; url: string } | null>(null)
  const [continuing, setContinuing] = useState(false)
  const [finished, setFinished] = useState(false)

  const url = new URL(window.location.href)
  const isError = url.searchParams.get('error')
  const errorReason = url.searchParams.get('errorReason')

  const getContext = async () => {
    try {
      const { data } = await axiosInstance.get<GetJointSigningContextResponse>(
        `${API_URL}/${JOINTSIGNING_SERVICE_PATH}/get-context`,
        { withCredentials: true }
      )

      const [next] = data?.waitingOnSignatories ?? []
      if (data?.enableNextSignatory && next?.nextSignatoryUrl) {
        setNextSignatory({
          fullName: `${next.firstName} ${next.lastName}`,
          url: next.nextSignatoryUrl,
        })
      }
    } catch (error) {
      devLog('Error fetching joint signing context:', error)
    }

    setIsLoading(false)
  }

  const handleContinueAs = () => {
    if (continuing || !nextSignatory) return
    setContinuing(true)
    forceRedirect(nextSignatory.url)
  }

  // Ends the process on this device: no redirect to the client's URL, the user is told to close
  // the tab instead (the hand-over button goes away with it).
  const handleFinish = () => {
    if (continuing) return
    setFinished(true)
  }

  useEffect(() => {
    sessionStorage.removeItem('finalTimestamp')
    store.InterfaceState.setSendBeforeunloadEvent(false)

    void getContext()
  }, [])

  if (isError) {
    return <VerifiedErrorEnpalPage errorReason={getVerifiedErrorReason(isError, errorReason)} />
  }

  if (isLoading) {
    return (
      <div className="page-container mobile-m-10">
        <LoadingSpinner />
      </div>
    )
  }

  return (
    <div className="page-container mobile-m-0">
      <div className="layout__content-container-with-scroll data-collection__success">
        <div className="data-collection__success-icon">
          <Icon
            type="check-circle"
            size="56px"
            color="var(--data-collection-success-color, #76be74)"
          />
        </div>
        <h1 className="header__container onboarding-header data-collection__success-title">
          {trans.title}
        </h1>
        <p className="generic__paragraph generic__paragraph-centered">{trans.description}</p>
        {finished && (
          <p className="generic__paragraph generic__paragraph-centered">
            <strong>{trans.closeTab}</strong>
          </p>
        )}
      </div>

      {!finished && (
        <div className="layout__content-container-bottom-fixed layout__content-container-bottom-fixed--stacked">
          {nextSignatory && (
            <Button onClick={handleContinueAs} dataTestid="affordability-check-continue-as">
              {continuing && <LoadingSpinner inButton />}
              <span
                className={cx(
                  'generic__button-text',
                  continuing && 'generic__button-text--loading'
                )}
              >
                {`${trans.continueAs} ${nextSignatory.fullName}`}
              </span>
            </Button>
          )}
          <Button
            onClick={handleFinish}
            className={cx(nextSignatory && 'button--secondary')}
            disabled={continuing}
            dataTestid="affordability-check-complete"
          >
            <span className="generic__button-text">{trans.finish}</span>
          </Button>
        </div>
      )}
      <div className="layout__mobile-spacer-for-content-container-bottom-fixed" />
    </div>
  )
})
